import { ref } from 'vue';
import { ElMessage } from 'element-plus';
import type { DesignReviewResultData } from '@/api/aiReviewApi';
import { pollDesignReviewResult } from '@/api/aiReviewApi';

const STORAGE_KEY = 'aipd-design-review-tasks';

export type StoredReviewTask = {
  taskId: string;
  status: 'polling' | 'done' | 'failed';
  updatedAt: number;
  result?: DesignReviewResultData;
  errorMessage?: string;
};

export const reviewTasksRef = ref<Record<string, StoredReviewTask>>({});

const pollingObjectKeys = new Set<string>();

function readAll(): Record<string, StoredReviewTask> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, StoredReviewTask>;
  } catch {
    return {};
  }
}

function writeAll(data: Record<string, StoredReviewTask>) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function hydrateAiReviewTasks() {
  reviewTasksRef.value = readAll();
}

export function setAiReviewPolling(objectKey: string, taskId: string) {
  const next = {
    ...reviewTasksRef.value,
    [objectKey]: { taskId, status: 'polling' as const, updatedAt: Date.now() },
  };
  reviewTasksRef.value = next;
  writeAll(next);
}

export function setAiReviewDone(objectKey: string, taskId: string, result: DesignReviewResultData) {
  const next = {
    ...reviewTasksRef.value,
    [objectKey]: { taskId, status: 'done' as const, updatedAt: Date.now(), result },
  };
  reviewTasksRef.value = next;
  writeAll(next);
}

export function setAiReviewFailed(objectKey: string, taskId: string, errorMessage: string) {
  const next = {
    ...reviewTasksRef.value,
    [objectKey]: {
      taskId,
      status: 'failed' as const,
      updatedAt: Date.now(),
      errorMessage,
    },
  };
  reviewTasksRef.value = next;
  writeAll(next);
}

export function removeAiReviewTask(objectKey: string) {
  const copy = { ...reviewTasksRef.value };
  delete copy[objectKey];
  reviewTasksRef.value = copy;
  writeAll(copy);
}

/** 图纸移动后迁移智审任务缓存键 */
export function remapAiReviewTaskKey(oldKey: string, newKey: string) {
  if (!oldKey || !newKey || oldKey === newKey) return;
  const prev = reviewTasksRef.value[oldKey];
  if (!prev) return;
  const copy = { ...reviewTasksRef.value };
  delete copy[oldKey];
  copy[newKey] = { ...prev, updatedAt: Date.now() };
  reviewTasksRef.value = copy;
  writeAll(copy);
}

/** 恢复未完成任务的后台轮询（防重复） */
export function runAiReviewPollIfNeeded(
  objectKey: string,
  taskId: string,
  onProgress?: (data: import('@/api/aiReviewApi').DesignReviewResultData) => void,
) {
  if (pollingObjectKeys.has(objectKey)) return;
  pollingObjectKeys.add(objectKey);
  void (async () => {
    try {
      const data = await pollDesignReviewResult(taskId, 1500, onProgress);
      setAiReviewDone(objectKey, taskId, data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setAiReviewFailed(objectKey, taskId, msg || '智审失败');
      ElMessage.error(msg || '智审失败');
    } finally {
      pollingObjectKeys.delete(objectKey);
    }
  })();
}

export function resumePollingTasksFromStorage() {
  for (const [objectKey, t] of Object.entries(reviewTasksRef.value)) {
    if (t.status === 'polling' && t.taskId) {
      runAiReviewPollIfNeeded(objectKey, t.taskId);
    }
  }
}
